import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { Button } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useFieldArray } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import IconAdd from "~/components/Icons/icon-add";
import IconClose from "~/components/Icons/icon-close";
import { useCourses } from "~/entity/Courses";
import { useGetLocationData } from "~/hooks/locationData";
import { optionsStatus } from "~/pages/Editor/EditorCatalogEducationalPrograms/options";
import { SelectOptions } from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import { OpenModal } from "~/pages/Editor/type";
import { clearFormControlCache } from "~/shared/lib/ClearFormControlCache/clearFormControlCache";
import { Field } from "~/shared/ui/Field";

import { toServer } from "./lib/toServer";
import { schema } from "./validation";

import s from "./CoursesManagement.less";

export type FormSubmit = {
  name: string;
  code: string;
  status: SelectOptions;
  codeIS: string;
  level: SelectOptions;
  certificateValidityPeriod: number | null;
  replacementCourseIds:
    | { value: number | undefined; label: string }[]
    | undefined;
};

interface ICoursesManagementProps {
  formId: string;
  refetch: () => void;
  closeModal: () => void;
  defaultValues: Record<string, unknown>;
  levelSelectOptionsList: { label: string; value: number }[];
  isLoading?: boolean;
}

export const CoursesManagement = ({
  formId,
  refetch,
  closeModal,
  defaultValues,
  levelSelectOptionsList,
  isLoading,
}: ICoursesManagementProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useGetLocationData();
  const isEdit = openModal === OpenModal.Edit && currentId;

  const { coursesCreate, coursesPut, coursesSelect } = useCourses();

  const { courses, searchCourses, onScrollBottomCourses } = coursesSelect({
    pageSize: 25,
  });

  const {
    handleSubmit,
    control,
    watch,
    errors,
    formState: { isSubmitting },
  } = useForm<FormSubmit>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "replacementCourseIds",
  });

  const selectedCoursesIds =
    watch("replacementCourseIds")
      ?.map((c) => c?.value)
      .filter(Boolean) || [];

  const submit = async (data: FormSubmit) => {
    const payload = toServer(data);
    if (openModal === OpenModal.Edit && currentId) {
      await coursesPut({ instanceId: Number(currentId), ...payload });
    } else {
      await coursesCreate(payload);
    }

    closeModal?.();
    refetch?.();
  };

  const getFilteredCourses = (
    options: { label: string; value: string | number }[],
    instanceIdArr: number[],
  ) => {
    return options.filter(
      (course) => !instanceIdArr.includes(Number(course.value)),
    );
  };

  const filteredOptions = getFilteredCourses(courses, [
    ...selectedCoursesIds,
    Number(currentId),
  ]);

  const appendAltCourse = () => {
    const currentReplacementCourseIds = watch("replacementCourseIds") ?? [];
    const lastSelectValIsSet =
      currentReplacementCourseIds[currentReplacementCourseIds.length - 1];
    if (lastSelectValIsSet?.value || !lastSelectValIsSet) {
      append({ value: null, label: "Не выбрано" });
    }
  };

  // Подгружаем следующую страницу курсов только при доскролле списка до низа
  const handlePopupScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      onScrollBottomCourses();
    }
  };

  useEffect(() => {
    clearFormControlCache(control);
  }, [fields]);

  const formIsLoading = isLoading || isSubmitting;

  return (
    <form id={formId} className={s.form} onSubmit={handleSubmit(submit)}>
      <div className={s.items}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("cep.name")}
          error={errors["name"]?.message}
          disabled={formIsLoading}
        />
        <Field
          type="input"
          name="code"
          control={control}
          label={t("cep.code")}
          error={errors["code"]?.message}
          disabled={formIsLoading}
        />
        <Field
          type="input"
          name="certificateValidityPeriod"
          control={control}
          label={t("cep.certificateValidityPeriod")}
          error={errors["certificateValidityPeriod"]?.message}
          disabled={formIsLoading}
        />
        <Field
          type="input"
          name="codeIS"
          control={control}
          label={t("cep.courses_table.codeIS")}
          error={errors["codeIS"]?.message}
          disabled={formIsLoading}
        />
        <Field
          type="select"
          name="status"
          control={control}
          label={t("cep.status")}
          options={optionsStatus}
          labelInValue
          error={errors["status"]?.value?.message}
          disabled={formIsLoading}
        />
        <Field
          type="select"
          name="level"
          control={control}
          label={t("cep.level")}
          options={levelSelectOptionsList}
          labelInValue
          error={errors["level"]?.value?.message}
          disabled={formIsLoading}
        />

        {isEdit && (
          <div className={s.item}>
            <div className={s.name}>{t("cep.alternativeCourses")}</div>

            {fields.map((field, index) => {
              const deleteDefaultValIfSet = !!field.value;
              let decoratedOptions = filteredOptions;
              if (deleteDefaultValIfSet) {
                decoratedOptions = getFilteredCourses(filteredOptions, [
                  undefined,
                ]);
              }

              return (
                <div key={field.id} className={s.altCourseRow}>
                  <Field
                    type="select"
                    name={`replacementCourseIds.${index}`}
                    control={control}
                    options={decoratedOptions}
                    labelInValue
                    showSearch
                    filterOption={false}
                    onSearch={searchCourses}
                    onPopupScroll={handlePopupScroll}
                    disabled={formIsLoading}
                    wrapperClassName={s.altCourseSelect}
                  />
                  <Button
                    size={"middle"}
                    color={"secondary"}
                    icon={<IconClose fill={"#fff"} />}
                    onClick={() => remove(index)}
                    disabled={formIsLoading}
                  />
                </div>
              );
            })}

            <Button
              size={"middle"}
              icon={<IconAdd fill="#fff" />}
              onClick={appendAltCourse}
              disabled={formIsLoading}
              className={s.addAltBtn}
            >
              {t("cep.addAlternativeCourse")}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};
